import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { HttpClient, HttpEventType } from "@angular/common/http";
import { catchError, map, takeWhile, tap } from "rxjs/operators";
import { Nullable } from "../support/nullable";
import { blobToDataUrl } from "../support/blob";

import { sntLog } from "src/app/common/logger";

const logger = sntLog("DownloadService");
interface DownloadEvent {
  type: HttpEventType;
  progress: number;
  data: Nullable<Blob>;
  error: Nullable<any>;
}

interface DownloadDataUrlEvent {
  type: HttpEventType;
  progress: number;
  data: Nullable<string>;
  error: Nullable<any>;
}

@Injectable()
export class DownloadService {
  constructor(private http: HttpClient) {}

  downloadAsDataUrl(url: string): Observable<DownloadDataUrlEvent> {
    return new Observable<DownloadDataUrlEvent>((subscriber) => {
      this.http
        .get(url, {
          observe: "events",
          reportProgress: true,
          responseType: "blob"
        })
        .pipe(
          map((httpEvent) => {
            switch (httpEvent.type) {
              case HttpEventType.DownloadProgress: {
                return <DownloadEvent>{
                  type: httpEvent.type,
                  progress: Math.round(
                    100 * (httpEvent.loaded / (httpEvent.total || 0))
                  ),
                  data: null
                };
              }
              case HttpEventType.Response: {
                return <DownloadEvent>{
                  type: httpEvent.type,
                  progress: 100,
                  data: httpEvent.body
                };
              }
              default: {
                return <DownloadEvent>{
                  type: httpEvent.type,
                  progress: 0,
                  data: null
                };
              }
            }
          }),
          catchError((error) => {
            logger.error(error);
            return of(<DownloadEvent>{
              progress: -1,
              data: null,
              error
            });
          }),
          tap((downloadEvent: DownloadEvent) => {
            if (downloadEvent.data) {
              blobToDataUrl(downloadEvent.data)
                .then((dataUrl) => {
                  subscriber.next({
                    type: downloadEvent.type,
                    data: dataUrl,
                    error: null,
                    progress: downloadEvent.progress
                  });
                })
                .catch((error) => {
                  subscriber.next({
                    type: downloadEvent.type,
                    data: null,
                    error: error,
                    progress: downloadEvent.progress
                  });
                });
            } else {
              subscriber.next({
                type: downloadEvent.type,
                data: null,
                error: downloadEvent.error,
                progress: downloadEvent.progress
              });
            }
          }),
          takeWhile((downloadEvent: DownloadEvent) => {
            return (
              downloadEvent.type != HttpEventType.Response &&
              !downloadEvent.error
            );
          })
        )
        .subscribe();
    });
  }
}
